import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import myAxios from "../AxiosUrl";
import styles from "./LiveScores.module.css";
import { useSocket } from "../../services/GlobalSocket";

const LiveScores = () => {
  const [activeTab, setActiveTab] = useState("live");
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [liveMatchModal, setLiveMatchModal] = useState(null);
  const [liveMatchData, setLiveMatchData] = useState(null);
  const [liveMatchLoading, setLiveMatchLoading] = useState(false);
  const [liveMatchError, setLiveMatchError] = useState(null);
  const [systemMetrics, setSystemMetrics] = useState(null);
  const [modalActiveTab, setModalActiveTab] = useState("events");
  const [searchQuery, setSearchQuery] = useState("");
  const socket = useSocket();

  // Fetch dashboard data
  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const response = await myAxios.get("/live/dashboard");
      if (response.data.success) {
        console.log("LiveScores: Received data:", {
          matchesCount: response.data.data.matches?.length,
          stats: response.data.data.stats,
          sampleMatch: response.data.data.matches?.[0],
        });
        setDashboardData(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching dashboard:", error);
      setDashboardData({ matches: [], stats: {} });
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch system metrics
  const fetchSystemMetrics = useCallback(async () => {
    try {
      const response = await myAxios.get("/live/metrics");
      if (response.data.success) {
        setSystemMetrics(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching metrics:", error);
    }
  }, []);

  // Initialize data
  useEffect(() => {
    fetchDashboard();
    fetchSystemMetrics();
  }, [fetchDashboard, fetchSystemMetrics]);

  // WebSocket subscription
  useEffect(() => {
    if (!socket || !socket.isConnected) return undefined;

    const handleLiveRefresh = () => {
      fetchDashboard();

      if (!liveMatchModal?.matchId) return;

      myAxios
        .get(`/live/match/${liveMatchModal.matchId}`)
        .then((response) => {
          if (response.data?.success) {
            setLiveMatchData(response.data.data);
          }
        })
        .catch((error) => {
          console.error("Error refreshing live match data:", error);
        });
    };

    const handleSubscribeError = (message = {}) => {
      console.error("Live subscription error:", message);
    };

    socket.subscribe("LiveDataRefresh", handleLiveRefresh);
    socket.subscribe("LiveScreenDataUpdate", handleLiveRefresh);
    socket.subscribe("live_subscribe_error", handleSubscribeError);
    socket.send({ type: "subscribe_live_updates" });

    return () => {
      socket.unsubscribe("LiveDataRefresh", handleLiveRefresh);
      socket.unsubscribe("LiveScreenDataUpdate", handleLiveRefresh);
      socket.unsubscribe("live_subscribe_error", handleSubscribeError);
      socket.send({ type: "unsubscribe_live_updates" });
    };
  }, [socket, socket?.isConnected, fetchDashboard, liveMatchModal]);

  // Control cron job
  const handleCronControl = async (action) => {
    try {
      await myAxios.post("/live/control", { action });
      fetchSystemMetrics();
    } catch (error) {
      console.error("Error controlling cron:", error);
    }
  };

  // Force update
  const handleForceUpdate = async () => {
    try {
      await myAxios.post("/live/control", { action: "force-update" });
      await fetchDashboard();
    } catch (error) {
      console.error("Error forcing update:", error);
    }
  };

  // Handle view live match
  const handleViewLive = async (match) => {
    try {
      setLiveMatchModal(match);
      setLiveMatchLoading(true);
      setLiveMatchError(null);

      // Fetch initial live data
      const response = await myAxios.get(`/live/match/${match.matchId}`);
      if (response.data.success) {
        setLiveMatchData(response.data.data);
        setLiveMatchError(null);
      } else {
        setLiveMatchError("Failed to load live match data");
      }

      // Subscribe to live updates for this match
      if (socket && socket.isConnected) {
        socket.send({ type: "subscribe_live_updates", matchId: match.matchId });
      }
    } catch (error) {
      console.error("Error fetching live match data:", error);
      setLiveMatchError(error.response?.data?.message || "Failed to load live match data");
    } finally {
      setLiveMatchLoading(false);
    }
  };

  // Handle close live modal
  const handleCloseLiveModal = () => {
    // Unsubscribe from match updates
    if (socket && socket.isConnected && liveMatchModal) {
      socket.send({
        type: "unsubscribe_live_updates",
        matchId: liveMatchModal.matchId,
      });
    }
    setLiveMatchModal(null);
    setLiveMatchData(null);
    setLiveMatchLoading(false);
    setLiveMatchError(null);
  };

  // Filter matches
  const filterMatches = (matches) => {
    if (!matches) return [];

    let filtered = [...matches];

    // Filter by tab - Check isLive flag first, then fall back to status fields
    if (activeTab === "live") {
      filtered = filtered.filter((m) => {
        // First check if match has isLive flag set to true
        if (m.isLive === true) return true;

        // Fallback to checking status fields
        const status = (m.matchStatus || m.status || "").toLowerCase();
        return [
          "playing",
          "firsthalf",
          "secondhalf",
          "halftime",
          "live",
        ].includes(status);
      });
    } else if (activeTab === "upcoming") {
      filtered = filtered.filter((m) => {
        // Exclude live matches from upcoming
        if (m.isLive === true) return false;

        const status = (m.matchStatus || m.status || "").toLowerCase();
        return status === "fixture";
      });
    } else if (activeTab === "finished") {
      filtered = filtered.filter((m) => {
        // Exclude live matches from finished
        if (m.isLive === true) return false;

        const status = (m.matchStatus || m.status || "").toLowerCase();
        return ["played", "fulltime", "finished", "ft"].includes(status);
      });
    }

    // Filter by search
    if (searchQuery) {
      filtered = filtered.filter(
        (m) =>
          m.home?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          m.away?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          m.competition?.name?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    return filtered;
  };

  // Calculate time left using proper timezone handling
  const getTimeLeft = (match) => {
    if (!match.localDate || !match.localTime) return null;

    try {
      // Parse the date and time in Turkish timezone (UTC+3)
      // localDate is in format "2025-09-17" and localTime is "20:00:00"
      const matchDateTime = new Date(`${match.localDate}T${match.localTime}`);

      // Get current time
      const now = new Date();

      // Calculate difference
      const diff = matchDateTime - now;

      if (diff <= 0) return null;

      const totalMinutes = Math.floor(diff / (1000 * 60));
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;

      if (hours >= 24) {
        const days = Math.floor(hours / 24);
        const remainingHours = hours % 24;
        return `${days}d ${remainingHours}h ${minutes}m`;
      }
      if (hours > 0) {
        return `${hours}h ${minutes}m`;
      }
      return `${minutes}m`;
    } catch (error) {
      console.error("Error calculating time left:", error);
      return null;
    }
  };

  // Get status badge style - Case-insensitive matching
  const getStatusStyle = (status) => {
    const statusLower = (status || "").toLowerCase();
    if (["playing", "firsthalf", "secondhalf", "live"].includes(statusLower))
      return styles.live;
    if (["halftime"].includes(statusLower)) return styles.halftime;
    if (["played", "fulltime", "finished", "ft"].includes(statusLower))
      return styles.finished;
    if (statusLower === "fixture") return styles.upcoming;
    return styles.upcoming;
  };

  // Render match card
  const renderMatchCard = (match) => (
    <div
      key={match.matchId}
      className={styles.matchCard}
      onClick={() => setSelectedMatch(match)}
    >
      <div className={styles.matchHeader}>
        <div className={styles.tournamentInfo}>
          <div className={styles.tournamentName}>
            {match.competition?.name || "Unknown Competition"}
          </div>
          <div className={styles.matchDate}>
            {match.localDate
              ? new Date(match.localDate).toLocaleDateString("en-US", {
                  timeZone: "Europe/Istanbul",
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })
              : match.localDate}{" "}
            •{" "}
            {match.localTime
              ? match.localTime.slice(0, 5)
              : match.time?.slice(0, 5)}
          </div>
        </div>

        <div
          className={`${styles.statusBadge} ${getStatusStyle(
            match.matchStatus
          )}`}
        >
          {["Playing", "FirstHalf", "SecondHalf", "Live"].includes(
            match.matchStatus
          ) && <span className={styles.liveIndicator} />}
          {match.matchStatus === "Playing"
            ? "LIVE"
            : match.matchStatus === "Fixture"
            ? "Upcoming"
            : match.matchStatus || "Upcoming"}
        </div>
      </div>

      <div className={styles.teamsContainer}>
        <div className={styles.team}>
          <div className={styles.teamLogo}>
            {match.home?.logo ? (
              <img
                src={match.home.logo}
                alt={match.home.name}
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextSibling.style.display = "flex";
                }}
              />
            ) : null}
            <span
              className={`material-icons ${styles.teamPlaceholder}`}
              style={{ display: match.home?.logo ? "none" : "flex" }}
            >
              shield
            </span>
          </div>
          <div className={styles.teamInfo}>
            <h3 className={styles.teamName}>
              {match.home?.name || "Home Team"}
            </h3>
            {match.home?.form && (
              <p className={styles.teamForm}>Form: {match.home.form}</p>
            )}
          </div>
        </div>

        <div className={styles.scoreContainer}>
          {match.score ? (
            <>
              <span className={styles.score}>{match.score.home || 0}</span>
              <span className={styles.scoreSeparator}>-</span>
              <span className={styles.score}>{match.score.away || 0}</span>
            </>
          ) : (
            <span className={styles.noScore}>vs</span>
          )}
        </div>

        <div className={`${styles.team} ${styles.away}`}>
          <div className={styles.teamLogo}>
            {match.away?.logo ? (
              <img
                src={match.away.logo}
                alt={match.away.name}
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextSibling.style.display = "flex";
                }}
              />
            ) : null}
            <span
              className={`material-icons ${styles.teamPlaceholder}`}
              style={{ display: match.away?.logo ? "none" : "flex" }}
            >
              shield
            </span>
          </div>
          <div className={styles.teamInfo}>
            <h3 className={styles.teamName}>
              {match.away?.name || "Away Team"}
            </h3>
            {match.away?.form && (
              <p className={styles.teamForm}>Form: {match.away.form}</p>
            )}
          </div>
        </div>
      </div>

      <div className={styles.matchFooter}>
        <div className={styles.matchInfo}>
          {match.venue && (
            <div className={styles.infoItem}>
              <span className="material-icons">stadium</span>
              <span>{match.venue}</span>
            </div>
          )}
          {activeTab === "upcoming" && getTimeLeft(match) && (
            <div className={styles.infoItem}>
              <span className="material-icons">schedule</span>
              <span className={styles.timeLeft}>{getTimeLeft(match)}</span>
            </div>
          )}
        </div>

        {match.isLive ? (
          <button
            className={styles.viewLiveButton}
            onClick={(e) => {
              e.stopPropagation();
              handleViewLive(match);
            }}
          >
            <span className="material-icons">play_circle</span>
            View Live
          </button>
        ) : (
          <button className={styles.viewButton}>View Details</button>
        )}
      </div>
    </div>
  );

  const stats = dashboardData?.stats || {};
  const filteredMatches = filterMatches(dashboardData?.matches);

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <p className={styles.loadingText}>Loading matches...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.headerTop}>
            <div className={styles.logo}>
              <div className={styles.logoIcon}>T</div>
              <div className={styles.title}>
                <span className={styles.titleMain}>Live Match Center</span>
                <span className={styles.titleSub}>Real-time match updates</span>
              </div>
            </div>

            <div className={styles.connectionStatus}>
              <span className={styles.statusDot} style={{
                backgroundColor: socket?.connected ? '#4CAF50' : '#F44336'
              }} />
              <span>{socket?.connected ? 'WebSocket connected' : 'WebSocket disconnected'}</span>
              {dashboardData?.timestamp && (
                <span className={styles.lastUpdate}>
                  • Updated:{" "}
                  {new Date(dashboardData.timestamp).toLocaleTimeString(
                    "en-US",
                    {
                      timeZone: "Europe/Istanbul",
                      hour12: false,
                    }
                  )}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className={styles.statsContainer}>
        <div className={`${styles.statCard} ${styles.live}`}>
          <div className={`${styles.statIcon} ${styles.live}`}>
            <span className="material-icons">sports_score</span>
          </div>
          <div className={styles.statContent}>
            <h2 className={styles.statNumber}>{stats.liveNow || 0}</h2>
            <p className={styles.statLabel}>Live Now</p>
          </div>
        </div>

        <div className={`${styles.statCard} ${styles.upcoming}`}>
          <div className={`${styles.statIcon} ${styles.upcoming}`}>
            <span className="material-icons">schedule</span>
          </div>
          <div className={styles.statContent}>
            <h2 className={styles.statNumber}>{stats.upcoming || 0}</h2>
            <p className={styles.statLabel}>Upcoming</p>
          </div>
        </div>

        <div className={`${styles.statCard} ${styles.finished}`}>
          <div className={`${styles.statIcon} ${styles.finished}`}>
            <span className="material-icons">check_circle</span>
          </div>
          <div className={styles.statContent}>
            <h2 className={styles.statNumber}>{stats.finished || 0}</h2>
            <p className={styles.statLabel}>Finished</p>
          </div>
        </div>

        <div className={`${styles.statCard} ${styles.total}`}>
          <div className={`${styles.statIcon} ${styles.total}`}>
            <span className="material-icons">calendar_month</span>
          </div>
          <div className={styles.statContent}>
            <h2 className={styles.statNumber}>{stats.totalMatches || 0}</h2>
            <p className={styles.statLabel}>Today's Matches</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={styles.mainContent}>
        {/* Tabs */}
        <div className={styles.tabsContainer}>
          <button
            className={`${styles.tabButton} ${
              activeTab === "live" ? styles.active : ""
            }`}
            onClick={() => setActiveTab("live")}
          >
            <span className={`material-icons ${styles.tabIcon}`}>
              sports_score
            </span>
            <span>Live Matches</span>
            {stats.liveNow > 0 && (
              <span className={styles.tabBadge}>{stats.liveNow}</span>
            )}
          </button>

          <button
            className={`${styles.tabButton} ${
              activeTab === "upcoming" ? styles.active : ""
            }`}
            onClick={() => setActiveTab("upcoming")}
          >
            <span className={`material-icons ${styles.tabIcon}`}>schedule</span>
            <span>Upcoming</span>
            <span className={styles.tabBadge}>{stats.upcoming || 0}</span>
          </button>

          <button
            className={`${styles.tabButton} ${
              activeTab === "finished" ? styles.active : ""
            }`}
            onClick={() => setActiveTab("finished")}
          >
            <span className={`material-icons ${styles.tabIcon}`}>
              check_circle
            </span>
            <span>Finished</span>
            <span className={styles.tabBadge}>{stats.finished || 0}</span>
          </button>

          <button
            className={`${styles.tabButton} ${
              activeTab === "system" ? styles.active : ""
            }`}
            onClick={() => setActiveTab("system")}
          >
            <span className={`material-icons ${styles.tabIcon}`}>settings</span>
            <span>System</span>
          </button>
        </div>

        {/* Search Bar */}
        {activeTab !== "system" && (
          <div className={styles.searchContainer}>
            <span className={`material-icons ${styles.searchIcon}`}>
              search
            </span>
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Search teams or competitions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        )}

        {/* System Control */}
        {activeTab === "system" && (
          <div className={styles.systemControl}>
            <div className={styles.systemHeader}>
              <h3 className={styles.systemTitle}>
                <span className="material-icons">settings</span>
                System Control
              </h3>
              <div className={styles.systemStatus}>
                <span className={styles.statusDot} />
                {systemMetrics?.cronActive ? "Active" : "Inactive"}
              </div>
            </div>

            <div className={styles.systemActions}>
              <button
                className={`${styles.controlButton} ${
                  systemMetrics?.cronActive ? styles.active : ""
                }`}
                onClick={() => handleCronControl("start")}
                disabled={systemMetrics?.cronActive}
              >
                <span className="material-icons">play_arrow</span>
                Start Cron
              </button>

              <button
                className={styles.controlButton}
                onClick={() => handleCronControl("stop")}
                disabled={!systemMetrics?.cronActive}
              >
                <span className="material-icons">stop</span>
                Stop Cron
              </button>

              <button
                className={styles.controlButton}
                onClick={() => handleCronControl("restart")}
              >
                <span className="material-icons">refresh</span>
                Restart
              </button>

              <button
                className={styles.controlButton}
                onClick={handleForceUpdate}
              >
                <span className="material-icons">update</span>
                Force Update
              </button>
            </div>
          </div>
        )}

        {/* Match List */}
        {activeTab !== "system" && (
          <div className={styles.matchList}>
            {filteredMatches.length > 0 ? (
              <AnimatePresence>
                {filteredMatches.map((match) => (
                  <motion.div
                    key={match.matchId}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    {renderMatchCard(match)}
                  </motion.div>
                ))}
              </AnimatePresence>
            ) : (
              <div className={styles.emptyState}>
                <span className={`material-icons ${styles.emptyIcon}`}>
                  sports_soccer
                </span>
                <h3 className={styles.emptyTitle}>
                  No{" "}
                  {activeTab === "live"
                    ? "Live"
                    : activeTab === "upcoming"
                    ? "Upcoming"
                    : "Finished"}{" "}
                  Matches
                </h3>
                <p className={styles.emptyText}>
                  {activeTab === "live" &&
                    "There are no matches currently being played"}
                  {activeTab === "upcoming" && "No upcoming matches scheduled"}
                  {activeTab === "finished" && "No matches have finished yet"}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Match Details Modal */}
      <AnimatePresence>
        {selectedMatch && (
          <motion.div
            className={styles.modalOverlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedMatch(null)}
          >
            <motion.div
              className={styles.modalContent}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={styles.modalHeader}>
                <h2 className={styles.modalTitle}>Match Details</h2>
                <button
                  className={styles.closeButton}
                  onClick={() => setSelectedMatch(null)}
                >
                  <span className="material-icons">close</span>
                </button>
              </div>

              <div className={styles.modalBody}>
                {/* Match Header */}
                <div className={styles.modalMatchHeader}>
                  <div className={styles.modalCompetition}>
                    <span className="material-icons">emoji_events</span>
                    <span>
                      {selectedMatch.competition?.name || "Unknown Competition"}
                    </span>
                  </div>
                  <div
                    className={`${styles.modalStatusBadge} ${getStatusStyle(
                      selectedMatch.matchStatus || selectedMatch.status
                    )}`}
                  >
                    {selectedMatch.matchStatus === "Playing" ||
                    selectedMatch.status === "playing"
                      ? "LIVE"
                      : selectedMatch.matchStatus === "Fixture" ||
                        selectedMatch.status === "fixture"
                      ? "UPCOMING"
                      : selectedMatch.matchStatus === "Played" ||
                        selectedMatch.status === "played"
                      ? "FINISHED"
                      : selectedMatch.matchStatus ||
                        selectedMatch.status ||
                        "UPCOMING"}
                  </div>
                </div>

                {/* Teams Section */}
                <div className={styles.modalTeamsSection}>
                  <div className={styles.modalTeam}>
                    <div className={styles.modalTeamLogo}>
                      {selectedMatch.home?.logo ? (
                        <img
                          src={selectedMatch.home.logo}
                          alt={selectedMatch.home.name}
                        />
                      ) : (
                        <span className="material-icons">shield</span>
                      )}
                    </div>
                    <h3 className={styles.modalTeamName}>
                      {selectedMatch.home?.name || "Home Team"}
                    </h3>
                    <div className={styles.modalTeamCode}>
                      {selectedMatch.home?.code || ""}
                    </div>
                    <div className={styles.modalTeamType}>HOME</div>
                    {selectedMatch.home?.form && (
                      <div className={styles.modalTeamForm}>
                        Form: {selectedMatch.home.form}
                      </div>
                    )}
                  </div>

                  <div className={styles.modalScore}>
                    {selectedMatch.score ? (
                      <>
                        <span className={styles.modalScoreNumber}>
                          {selectedMatch.score.home || 0}
                        </span>
                        <span className={styles.modalScoreSeparator}>:</span>
                        <span className={styles.modalScoreNumber}>
                          {selectedMatch.score.away || 0}
                        </span>
                      </>
                    ) : (
                      <div className={styles.modalVs}>VS</div>
                    )}
                  </div>

                  <div className={styles.modalTeam}>
                    <div className={styles.modalTeamLogo}>
                      {selectedMatch.away?.logo ? (
                        <img
                          src={selectedMatch.away.logo}
                          alt={selectedMatch.away.name}
                        />
                      ) : (
                        <span className="material-icons">shield</span>
                      )}
                    </div>
                    <h3 className={styles.modalTeamName}>
                      {selectedMatch.away?.name || "Away Team"}
                    </h3>
                    <div className={styles.modalTeamCode}>
                      {selectedMatch.away?.code || ""}
                    </div>
                    <div className={styles.modalTeamType}>AWAY</div>
                    {selectedMatch.away?.form && (
                      <div className={styles.modalTeamForm}>
                        Form: {selectedMatch.away.form}
                      </div>
                    )}
                  </div>
                </div>

                {/* Match Identifiers */}
                <div className={styles.modalInfoSection}>
                  <h4 className={styles.modalSectionTitle}>
                    Match Identifiers
                  </h4>
                  <div className={styles.modalInfoGrid}>
                    <div className={styles.modalInfoItem}>
                      <span className="material-icons">fingerprint</span>
                      <div>
                        <div className={styles.modalInfoLabel}>Match ID</div>
                        <div className={styles.modalInfoValue}>
                          {selectedMatch.matchId || "N/A"}
                        </div>
                      </div>
                    </div>

                    {selectedMatch.tmcl && (
                      <div className={styles.modalInfoItem}>
                        <span className="material-icons">tag</span>
                        <div>
                          <div className={styles.modalInfoLabel}>TMCL ID</div>
                          <div className={styles.modalInfoValue}>
                            {selectedMatch.tmcl}
                          </div>
                        </div>
                      </div>
                    )}

                    {selectedMatch.coverageLevel && (
                      <div className={styles.modalInfoItem}>
                        <span className="material-icons">
                          signal_cellular_alt
                        </span>
                        <div>
                          <div className={styles.modalInfoLabel}>
                            Coverage Level
                          </div>
                          <div className={styles.modalInfoValue}>
                            Level {selectedMatch.coverageLevel}
                          </div>
                        </div>
                      </div>
                    )}

                    {selectedMatch.optaBetting && (
                      <div className={styles.modalInfoItem}>
                        <span className="material-icons">casino</span>
                        <div>
                          <div className={styles.modalInfoLabel}>
                            Betting Available
                          </div>
                          <div className={styles.modalInfoValue}>
                            {selectedMatch.optaBetting === "1" ? "Yes" : "No"}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Match Schedule */}
                <div className={styles.modalInfoSection}>
                  <h4 className={styles.modalSectionTitle}>
                    Schedule Information
                  </h4>
                  <div className={styles.modalInfoGrid}>
                    <div className={styles.modalInfoItem}>
                      <span className="material-icons">calendar_today</span>
                      <div>
                        <div className={styles.modalInfoLabel}>Date</div>
                        <div className={styles.modalInfoValue}>
                          {selectedMatch.localDate
                            ? new Date(
                                selectedMatch.localDate
                              ).toLocaleDateString("en-US", {
                                timeZone: "Europe/Istanbul",
                                weekday: "long",
                                month: "long",
                                day: "numeric",
                                year: "numeric",
                              })
                            : "TBD"}
                        </div>
                      </div>
                    </div>

                    <div className={styles.modalInfoItem}>
                      <span className="material-icons">schedule</span>
                      <div>
                        <div className={styles.modalInfoLabel}>
                          Local Time (Turkey)
                        </div>
                        <div className={styles.modalInfoValue}>
                          {selectedMatch.localTime
                            ? selectedMatch.localTime.slice(0, 5)
                            : "TBD"}{" "}
                          (UTC+3)
                        </div>
                      </div>
                    </div>

                    {selectedMatch.time && (
                      <div className={styles.modalInfoItem}>
                        <span className="material-icons">public</span>
                        <div>
                          <div className={styles.modalInfoLabel}>UTC Time</div>
                          <div className={styles.modalInfoValue}>
                            {selectedMatch.time.slice(0, 5)} UTC
                          </div>
                        </div>
                      </div>
                    )}

                    {selectedMatch.week && (
                      <div className={styles.modalInfoItem}>
                        <span className="material-icons">event_repeat</span>
                        <div>
                          <div className={styles.modalInfoLabel}>Matchweek</div>
                          <div className={styles.modalInfoValue}>
                            Week {selectedMatch.week}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Venue Information */}
                {(selectedMatch.venue || selectedMatch.venueInfo) && (
                  <div className={styles.modalInfoSection}>
                    <h4 className={styles.modalSectionTitle}>Venue Details</h4>
                    <div className={styles.modalInfoGrid}>
                      {selectedMatch.venue && (
                        <div className={styles.modalInfoItem}>
                          <span className="material-icons">stadium</span>
                          <div>
                            <div className={styles.modalInfoLabel}>Stadium</div>
                            <div className={styles.modalInfoValue}>
                              {selectedMatch.venue}
                            </div>
                          </div>
                        </div>
                      )}

                      {selectedMatch.venueInfo?.longName && (
                        <div className={styles.modalInfoItem}>
                          <span className="material-icons">location_on</span>
                          <div>
                            <div className={styles.modalInfoLabel}>
                              Full Name
                            </div>
                            <div className={styles.modalInfoValue}>
                              {selectedMatch.venueInfo.longName}
                            </div>
                          </div>
                        </div>
                      )}

                      {selectedMatch.venueInfo?.city && (
                        <div className={styles.modalInfoItem}>
                          <span className="material-icons">location_city</span>
                          <div>
                            <div className={styles.modalInfoLabel}>City</div>
                            <div className={styles.modalInfoValue}>
                              {selectedMatch.venueInfo.city}
                            </div>
                          </div>
                        </div>
                      )}

                      {selectedMatch.venueInfo?.neutral && (
                        <div className={styles.modalInfoItem}>
                          <span className="material-icons">balance</span>
                          <div>
                            <div className={styles.modalInfoLabel}>
                              Neutral Venue
                            </div>
                            <div className={styles.modalInfoValue}>
                              {selectedMatch.venueInfo.neutral === "yes"
                                ? "Yes"
                                : "No"}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Competition & Tournament */}
                <div className={styles.modalInfoSection}>
                  <h4 className={styles.modalSectionTitle}>
                    Competition Details
                  </h4>
                  <div className={styles.modalInfoGrid}>
                    {selectedMatch.competition && (
                      <>
                        <div className={styles.modalInfoItem}>
                          <span className="material-icons">emoji_events</span>
                          <div>
                            <div className={styles.modalInfoLabel}>
                              Competition
                            </div>
                            <div className={styles.modalInfoValue}>
                              {selectedMatch.competition.name}
                            </div>
                          </div>
                        </div>

                        {selectedMatch.competition.sponsorName && (
                          <div className={styles.modalInfoItem}>
                            <span className="material-icons">business</span>
                            <div>
                              <div className={styles.modalInfoLabel}>
                                Sponsor
                              </div>
                              <div className={styles.modalInfoValue}>
                                {selectedMatch.competition.sponsorName}
                              </div>
                            </div>
                          </div>
                        )}

                        {selectedMatch.competition.competitionCode && (
                          <div className={styles.modalInfoItem}>
                            <span className="material-icons">code</span>
                            <div>
                              <div className={styles.modalInfoLabel}>
                                Competition Code
                              </div>
                              <div className={styles.modalInfoValue}>
                                {selectedMatch.competition.competitionCode}
                              </div>
                            </div>
                          </div>
                        )}

                        {selectedMatch.competition.competitionFormat && (
                          <div className={styles.modalInfoItem}>
                            <span className="material-icons">category</span>
                            <div>
                              <div className={styles.modalInfoLabel}>
                                Format
                              </div>
                              <div className={styles.modalInfoValue}>
                                {selectedMatch.competition.competitionFormat}
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {selectedMatch.tournament && (
                      <>
                        <div className={styles.modalInfoItem}>
                          <span className="material-icons">sports_soccer</span>
                          <div>
                            <div className={styles.modalInfoLabel}>Season</div>
                            <div className={styles.modalInfoValue}>
                              {selectedMatch.tournament.name}
                            </div>
                          </div>
                        </div>

                        {selectedMatch.tournament.startDate && (
                          <div className={styles.modalInfoItem}>
                            <span className="material-icons">event</span>
                            <div>
                              <div className={styles.modalInfoLabel}>
                                Season Start
                              </div>
                              <div className={styles.modalInfoValue}>
                                {new Date(
                                  selectedMatch.tournament.startDate
                                ).toLocaleDateString("en-US", {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                })}
                              </div>
                            </div>
                          </div>
                        )}

                        {selectedMatch.tournament.endDate && (
                          <div className={styles.modalInfoItem}>
                            <span className="material-icons">
                              event_available
                            </span>
                            <div>
                              <div className={styles.modalInfoLabel}>
                                Season End
                              </div>
                              <div className={styles.modalInfoValue}>
                                {new Date(
                                  selectedMatch.tournament.endDate
                                ).toLocaleDateString("en-US", {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                })}
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {selectedMatch.stage && (
                      <>
                        <div className={styles.modalInfoItem}>
                          <span className="material-icons">flag</span>
                          <div>
                            <div className={styles.modalInfoLabel}>Stage</div>
                            <div className={styles.modalInfoValue}>
                              {selectedMatch.stage.name}
                            </div>
                          </div>
                        </div>

                        {selectedMatch.stage.formatId && (
                          <div className={styles.modalInfoItem}>
                            <span className="material-icons">
                              format_list_numbered
                            </span>
                            <div>
                              <div className={styles.modalInfoLabel}>
                                Stage Format
                              </div>
                              <div className={styles.modalInfoValue}>
                                {selectedMatch.stage.formatId}
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Live Data (if playing or finished) */}
                {selectedMatch.liveData && (
                  <>
                    <div className={styles.modalInfoSection}>
                      <h4 className={styles.modalSectionTitle}>Match Result</h4>
                      <div className={styles.modalInfoGrid}>
                        {selectedMatch.liveData.matchDetails?.scores && (
                          <>
                            {selectedMatch.liveData.matchDetails.scores.ht && (
                              <div className={styles.modalInfoItem}>
                                <span className="material-icons">timer</span>
                                <div>
                                  <div className={styles.modalInfoLabel}>
                                    Half Time Score
                                  </div>
                                  <div className={styles.modalInfoValue}>
                                    {
                                      selectedMatch.liveData.matchDetails.scores
                                        .ht.home
                                    }{" "}
                                    -{" "}
                                    {
                                      selectedMatch.liveData.matchDetails.scores
                                        .ht.away
                                    }
                                  </div>
                                </div>
                              </div>
                            )}

                            {selectedMatch.liveData.matchDetails.scores.ft && (
                              <div className={styles.modalInfoItem}>
                                <span className="material-icons">
                                  sports_score
                                </span>
                                <div>
                                  <div className={styles.modalInfoLabel}>
                                    Full Time Score
                                  </div>
                                  <div className={styles.modalInfoValue}>
                                    {
                                      selectedMatch.liveData.matchDetails.scores
                                        .ft.home
                                    }{" "}
                                    -{" "}
                                    {
                                      selectedMatch.liveData.matchDetails.scores
                                        .ft.away
                                    }
                                  </div>
                                </div>
                              </div>
                            )}
                          </>
                        )}

                        {selectedMatch.liveData.matchDetails
                          ?.matchLengthMin && (
                          <div className={styles.modalInfoItem}>
                            <span className="material-icons">timer</span>
                            <div>
                              <div className={styles.modalInfoLabel}>
                                Match Duration
                              </div>
                              <div className={styles.modalInfoValue}>
                                {
                                  selectedMatch.liveData.matchDetails
                                    .matchLengthMin
                                }{" "}
                                minutes
                              </div>
                            </div>
                          </div>
                        )}

                        {selectedMatch.liveData.matchDetails?.winner && (
                          <div className={styles.modalInfoItem}>
                            <span className="material-icons">emoji_events</span>
                            <div>
                              <div className={styles.modalInfoLabel}>
                                Winner
                              </div>
                              <div className={styles.modalInfoValue}>
                                {selectedMatch.liveData.matchDetails.winner ===
                                "home"
                                  ? selectedMatch.home?.name
                                  : selectedMatch.liveData.matchDetails
                                      .winner === "away"
                                  ? selectedMatch.away?.name
                                  : "Draw"}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Goals */}
                    {selectedMatch.liveData.goals &&
                      selectedMatch.liveData.goals.length > 0 && (
                        <div className={styles.modalInfoSection}>
                          <h4 className={styles.modalSectionTitle}>
                            <span
                              className="material-icons"
                              style={{
                                verticalAlign: "middle",
                                marginRight: "8px",
                              }}
                            >
                              sports_soccer
                            </span>
                            Goals
                          </h4>
                          <div className={styles.eventsList}>
                            {selectedMatch.liveData.goals.map((goal, index) => (
                              <div key={index} className={styles.eventItem}>
                                <div className={styles.eventTime}>
                                  {goal.timeMin}'
                                </div>
                                <div className={styles.eventDetails}>
                                  <div className={styles.eventPlayer}>
                                    <strong>
                                      {goal.scorerName || "Unknown"}
                                    </strong>
                                    {goal.assistPlayerName && (
                                      <span className={styles.assistText}>
                                        {" "}
                                        (Assist: {goal.assistPlayerName})
                                      </span>
                                    )}
                                  </div>
                                  <div className={styles.eventTeam}>
                                    {goal.contestantId ===
                                    selectedMatch.home?.id
                                      ? selectedMatch.home?.name
                                      : goal.contestantId ===
                                        selectedMatch.away?.id
                                      ? selectedMatch.away?.name
                                      : "Unknown Team"}
                                  </div>
                                  {goal.type && goal.type !== "Goal" && (
                                    <div className={styles.eventType}>
                                      {goal.type}
                                    </div>
                                  )}
                                </div>
                                <div className={styles.eventScore}>
                                  {goal.homeScore} - {goal.awayScore}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                    {/* Cards */}
                    {selectedMatch.liveData.cards &&
                      selectedMatch.liveData.cards.length > 0 && (
                        <div className={styles.modalInfoSection}>
                          <h4 className={styles.modalSectionTitle}>
                            <span
                              className="material-icons"
                              style={{
                                verticalAlign: "middle",
                                marginRight: "8px",
                              }}
                            >
                              style
                            </span>
                            Cards
                          </h4>
                          <div className={styles.eventsList}>
                            {selectedMatch.liveData.cards.map((card, index) => (
                              <div key={index} className={styles.eventItem}>
                                <div className={styles.eventTime}>
                                  {card.timeMin}'
                                </div>
                                <div
                                  className={`${styles.cardIcon} ${
                                    card.type === "YC" || card.type === "Yellow"
                                      ? styles.yellowCard
                                      : styles.redCard
                                  }`}
                                >
                                  <span className="material-icons">
                                    {card.type === "YC" ||
                                    card.type === "Yellow"
                                      ? "square"
                                      : card.type === "Y2C"
                                      ? "filter_2"
                                      : "square"}
                                  </span>
                                </div>
                                <div className={styles.eventDetails}>
                                  <div className={styles.eventPlayer}>
                                    <strong>
                                      {card.playerName || "Unknown"}
                                    </strong>
                                  </div>
                                  <div className={styles.eventTeam}>
                                    {card.contestantId ===
                                    selectedMatch.home?.id
                                      ? selectedMatch.home?.name
                                      : card.contestantId ===
                                        selectedMatch.away?.id
                                      ? selectedMatch.away?.name
                                      : "Unknown Team"}
                                  </div>
                                  {card.reason && (
                                    <div className={styles.cardReason}>
                                      {card.reason}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                    {/* Substitutions */}
                    {selectedMatch.liveData.substitutions &&
                      selectedMatch.liveData.substitutions.length > 0 && (
                        <div className={styles.modalInfoSection}>
                          <h4 className={styles.modalSectionTitle}>
                            <span
                              className="material-icons"
                              style={{
                                verticalAlign: "middle",
                                marginRight: "8px",
                              }}
                            >
                              swap_horiz
                            </span>
                            Substitutions
                          </h4>
                          <div className={styles.eventsList}>
                            {selectedMatch.liveData.substitutions.map(
                              (sub, index) => (
                                <div key={index} className={styles.eventItem}>
                                  <div className={styles.eventTime}>
                                    {sub.timeMin}'
                                  </div>
                                  <div className={styles.subIcon}>
                                    <span
                                      className="material-icons"
                                      style={{ color: "#4CAF50" }}
                                    >
                                      arrow_upward
                                    </span>
                                    <span
                                      className="material-icons"
                                      style={{ color: "#F44336" }}
                                    >
                                      arrow_downward
                                    </span>
                                  </div>
                                  <div className={styles.eventDetails}>
                                    <div className={styles.subPlayers}>
                                      <div className={styles.playerOut}>
                                        <span
                                          className="material-icons"
                                          style={{
                                            fontSize: "16px",
                                            color: "#F44336",
                                          }}
                                        >
                                          remove_circle
                                        </span>
                                        {sub.playerOffName || "Unknown"}
                                      </div>
                                      <div className={styles.playerIn}>
                                        <span
                                          className="material-icons"
                                          style={{
                                            fontSize: "16px",
                                            color: "#4CAF50",
                                          }}
                                        >
                                          add_circle
                                        </span>
                                        {sub.playerOnName || "Unknown"}
                                      </div>
                                    </div>
                                    <div className={styles.eventTeam}>
                                      {sub.contestantId ===
                                      selectedMatch.home?.id
                                        ? selectedMatch.home?.name
                                        : sub.contestantId ===
                                          selectedMatch.away?.id
                                        ? selectedMatch.away?.name
                                        : "Unknown Team"}
                                    </div>
                                  </div>
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      )}
                  </>
                )}

                {/* Additional Match Metadata */}
                <div className={styles.modalInfoSection}>
                  <h4 className={styles.modalSectionTitle}>
                    Additional Information
                  </h4>
                  <div className={styles.modalInfoGrid}>
                    {selectedMatch.lastUpdated && (
                      <div className={styles.modalInfoItem}>
                        <span className="material-icons">update</span>
                        <div>
                          <div className={styles.modalInfoLabel}>
                            Last Updated
                          </div>
                          <div className={styles.modalInfoValue}>
                            {new Date(selectedMatch.lastUpdated).toLocaleString(
                              "en-US",
                              {
                                timeZone: "Europe/Istanbul",
                                month: "short",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              }
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {selectedMatch.dataSource && (
                      <div className={styles.modalInfoItem}>
                        <span className="material-icons">source</span>
                        <div>
                          <div className={styles.modalInfoLabel}>
                            Data Source
                          </div>
                          <div className={styles.modalInfoValue}>
                            {selectedMatch.dataSource}
                          </div>
                        </div>
                      </div>
                    )}

                    {selectedMatch.isScheduleForLive !== undefined && (
                      <div className={styles.modalInfoItem}>
                        <span className="material-icons">live_tv</span>
                        <div>
                          <div className={styles.modalInfoLabel}>
                            Live Coverage
                          </div>
                          <div className={styles.modalInfoValue}>
                            {selectedMatch.isScheduleForLive
                              ? "Available"
                              : "Not Available"}
                          </div>
                        </div>
                      </div>
                    )}

                    {selectedMatch.numberOfGames && (
                      <div className={styles.modalInfoItem}>
                        <span className="material-icons">numbers</span>
                        <div>
                          <div className={styles.modalInfoLabel}>
                            Number of Games
                          </div>
                          <div className={styles.modalInfoValue}>
                            {selectedMatch.numberOfGames}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Enhanced Live Match Modal */}
        {liveMatchModal && (
          <motion.div
            className={styles.modalOverlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleCloseLiveModal}
          >
            <motion.div
              className={`${styles.modalContent} ${styles.enhancedLiveModal}`}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={styles.modalHeader}>
                <h2 className={styles.modalTitle}>
                  <span className={styles.liveIndicator}></span>
                  Live Match - Detailed View
                </h2>
                <button
                  className={styles.modalClose}
                  onClick={handleCloseLiveModal}
                >
                  <span className="material-icons">close</span>
                </button>
              </div>

              <div className={styles.enhancedModalBody}>
                {/* Loading State */}
                {liveMatchLoading && (
                  <div className={styles.loadingContainer}>
                    <div className={styles.spinner}></div>
                    <p>Loading live match data...</p>
                  </div>
                )}

                {/* Error State */}
                {liveMatchError && !liveMatchLoading && (
                  <div className={styles.errorContainer} style={{
                    padding: '40px',
                    textAlign: 'center',
                    color: '#F44336'
                  }}>
                    <span className="material-icons" style={{ fontSize: '48px', marginBottom: '16px' }}>error_outline</span>
                    <h3>{liveMatchError}</h3>
                    <button
                      onClick={() => handleViewLive(liveMatchModal)}
                      style={{
                        marginTop: '20px',
                        padding: '10px 20px',
                        background: '#667eea',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer'
                      }}
                    >
                      Retry
                    </button>
                  </div>
                )}

                {/* Match Content - Only show if not loading and no error */}
                {!liveMatchLoading && !liveMatchError && (
                  <>
                {/* Match Info Bar */}
                <div className={styles.liveMatchInfo}>
                  <div className={styles.matchCompetition}>
                    <span className="material-icons">emoji_events</span>
                    {liveMatchModal.competition?.name || "Competition"}
                  </div>
                  <div className={styles.matchVenue}>
                    <span className="material-icons">stadium</span>
                    {liveMatchModal.venue || "Stadium"}
                  </div>
                  <div className={styles.matchDate}>
                    <span className="material-icons">calendar_today</span>
                    {liveMatchModal.localDate || "Date"}
                  </div>
                </div>

                {/* Live Score Header */}
                <div className={styles.liveScoreHeader}>
                  <div className={styles.liveTeam}>
                    <img
                      src={liveMatchModal.home?.logo}
                      alt={liveMatchModal.home?.name}
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                    <h3>{liveMatchModal.home?.name}</h3>
                    {liveMatchModal.home?.form && (
                      <div className={styles.teamForm}>
                        {liveMatchModal.home.form
                          .split("")
                          .map((result, idx) => (
                            <span
                              key={idx}
                              className={`${styles.formBadge} ${
                                styles[`form${result}`]
                              }`}
                            >
                              {result}
                            </span>
                          ))}
                      </div>
                    )}
                  </div>
                  <div className={styles.liveScore}>
                    <div className={styles.scoreDisplay}>
                      <span className={styles.liveScoreHome}>
                        {liveMatchData?.liveData?.matchDetails?.scores?.total
                          ?.home ||
                          liveMatchData?.liveData?.matchDetails?.scores?.ft
                            ?.home ||
                          0}
                      </span>
                      <span className={styles.liveScoreSeparator}>-</span>
                      <span className={styles.liveScoreAway}>
                        {liveMatchData?.liveData?.matchDetails?.scores?.total
                          ?.away ||
                          liveMatchData?.liveData?.matchDetails?.scores?.ft
                            ?.away ||
                          0}
                      </span>
                    </div>
                    <div className={styles.liveMinute}>
                      <span className={styles.minuteNumber}>
                        {liveMatchData?.liveData?.matchDetails
                          ?.matchLengthMin || 90}
                        '
                      </span>
                      {liveMatchData?.liveData?.matchDetails?.period && (
                        <span className={styles.periodLabel}>
                          {typeof liveMatchData.liveData.matchDetails.period ===
                          "string"
                            ? liveMatchData.liveData.matchDetails.period
                            : liveMatchData.liveData.matchDetails.period?.id ||
                              `${
                                liveMatchData.liveData.matchDetails.period
                                  ?.lengthMin || 90
                              }'`}
                        </span>
                      )}
                    </div>
                    {/* Half Time Score if available */}
                    {liveMatchData?.liveData?.matchDetails?.scores?.ht && (
                      <div className={styles.htScore}>
                        HT: {liveMatchData.liveData.matchDetails.scores.ht.home}{" "}
                        - {liveMatchData.liveData.matchDetails.scores.ht.away}
                      </div>
                    )}
                  </div>
                  <div className={styles.liveTeam}>
                    <img
                      src={liveMatchModal.away?.logo}
                      alt={liveMatchModal.away?.name}
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                    <h3>{liveMatchModal.away?.name}</h3>
                    {liveMatchModal.away?.form && (
                      <div className={styles.teamForm}>
                        {liveMatchModal.away.form
                          .split("")
                          .map((result, idx) => (
                            <span
                              key={idx}
                              className={`${styles.formBadge} ${
                                styles[`form${result}`]
                              }`}
                            >
                              {result}
                            </span>
                          ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Enhanced Content Tabs */}
                <div className={styles.enhancedTabs}>
                  <button
                    className={`${styles.enhancedTab} ${
                      modalActiveTab === "events" ? styles.active : ""
                    }`}
                    onClick={() => setModalActiveTab("events")}
                  >
                    <span className="material-icons">sports_soccer</span>
                    Events
                  </button>
                  <button
                    className={`${styles.enhancedTab} ${
                      modalActiveTab === "lineups" ? styles.active : ""
                    }`}
                    onClick={() => setModalActiveTab("lineups")}
                  >
                    <span className="material-icons">group</span>
                    Lineups
                  </button>
                  <button
                    className={`${styles.enhancedTab} ${
                      modalActiveTab === "stats" ? styles.active : ""
                    }`}
                    onClick={() => setModalActiveTab("stats")}
                  >
                    <span className="material-icons">analytics</span>
                    Statistics
                  </button>
                  <button
                    className={`${styles.enhancedTab} ${
                      modalActiveTab === "fantasy" ? styles.active : ""
                    }`}
                    onClick={() => setModalActiveTab("fantasy")}
                  >
                    <span className="material-icons">star</span>
                    Fantasy Scores
                  </button>
                </div>

                {/* Events Tab */}
                {modalActiveTab === "events" && (
                  <div className={styles.eventsTabContent}>
                    {/* Goals Section */}
                    <div className={styles.statCard}>
                      <div className={styles.statCardHeader}>
                        <span className="material-icons">sports_soccer</span>
                        <h4>Goals</h4>
                        <span className={styles.badge}>
                          {liveMatchData?.liveData?.goals?.length || 0}
                        </span>
                      </div>
                      <div className={styles.statCardContent}>
                        {liveMatchData?.liveData?.goals &&
                        liveMatchData.liveData.goals.length > 0 ? (
                          liveMatchData.liveData.goals.map((goal, index) => (
                            <div key={index} className={styles.eventRow}>
                              <div className={styles.eventTime}>
                                {goal.timeMin}'
                              </div>
                              <div className={styles.eventInfo}>
                                <div className={styles.playerName}>
                                  <strong>
                                    {goal.scorerName || "Unknown"}
                                  </strong>
                                  {goal.assistPlayerName && (
                                    <span className={styles.assist}>
                                      {" "}
                                      (Assist: {goal.assistPlayerName})
                                    </span>
                                  )}
                                </div>
                                <div className={styles.teamBadge}>
                                  {goal.contestantId === liveMatchModal.home?.id
                                    ? liveMatchModal.home?.name
                                    : liveMatchModal.away?.name}
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className={styles.noData}>No goals scored</div>
                        )}
                      </div>
                    </div>

                    {/* Cards Section */}
                    <div className={styles.statCard}>
                      <div className={styles.statCardHeader}>
                        <span className="material-icons">style</span>
                        <h4>Cards</h4>
                        <span className={styles.badge}>
                          {liveMatchData?.liveData?.cards?.length || 0}
                        </span>
                      </div>
                      <div className={styles.statCardContent}>
                        {liveMatchData?.liveData?.cards &&
                        liveMatchData.liveData.cards.length > 0 ? (
                          liveMatchData.liveData.cards.map((card, index) => (
                            <div key={index} className={styles.eventRow}>
                              <div className={styles.eventTime}>
                                {card.timeMin}'
                              </div>
                              <div className={styles.eventInfo}>
                                <div className={styles.cardEventLayout}>
                                  <span
                                    className={`material-icons ${
                                      card.type === "YC" ||
                                      card.type === "Yellow"
                                        ? styles.yellowCardIcon
                                        : styles.redCardIcon
                                    }`}
                                  >
                                    {card.type === "Y2C"
                                      ? "filter_2"
                                      : "square"}
                                  </span>
                                  <div className={styles.cardEventContent}>
                                    <div className={styles.playerName}>
                                      <strong>
                                        {card.playerName || "Unknown"}
                                      </strong>
                                    </div>
                                    <div className={styles.teamBadge}>
                                      {card.contestantId ===
                                      liveMatchModal.home?.id
                                        ? liveMatchModal.home?.name
                                        : liveMatchModal.away?.name}
                                    </div>
                                    {card.cardReason && (
                                      <div className={styles.cardReason}>
                                        {card.cardReason}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className={styles.noData}>No cards shown</div>
                        )}
                      </div>
                    </div>

                    {/* Substitutions Section */}
                    <div className={styles.statCard}>
                      <div className={styles.statCardHeader}>
                        <span className="material-icons">swap_horiz</span>
                        <h4>Substitutions</h4>
                        <span className={styles.badge}>
                          {liveMatchData?.liveData?.substitute?.length || 0}
                        </span>
                      </div>
                      <div className={styles.statCardContent}>
                        {liveMatchData?.liveData?.substitute &&
                        liveMatchData.liveData.substitute.length > 0 ? (
                          liveMatchData.liveData.substitute.map(
                            (sub, index) => (
                              <div key={index} className={styles.eventRow}>
                                <div className={styles.eventTime}>
                                  {sub.timeMin}'
                                </div>
                                <div className={styles.eventInfo}>
                                  <div className={styles.substitutionInfo}>
                                    <div className={styles.playerOut}>
                                      <span
                                        className="material-icons"
                                        style={{
                                          color: "#F44336",
                                          fontSize: "16px",
                                        }}
                                      >
                                        remove_circle
                                      </span>
                                      {sub.playerOffName || "Unknown"}
                                    </div>
                                    <div className={styles.subArrow}>
                                      <span className="material-icons">
                                        arrow_forward
                                      </span>
                                    </div>
                                    <div className={styles.playerIn}>
                                      <span
                                        className="material-icons"
                                        style={{
                                          color: "#4CAF50",
                                          fontSize: "16px",
                                        }}
                                      >
                                        add_circle
                                      </span>
                                      {sub.playerOnName || "Unknown"}
                                    </div>
                                  </div>
                                  <div className={styles.teamBadge}>
                                    {sub.contestantId ===
                                    liveMatchModal.home?.id
                                      ? liveMatchModal.home?.name
                                      : liveMatchModal.away?.name}
                                  </div>
                                  {sub.subReason && (
                                    <div className={styles.subReason}>
                                      {sub.subReason}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )
                          )
                        ) : (
                          <div className={styles.noData}>No substitutions</div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Lineups Tab */}
                {modalActiveTab === "lineups" && (
                  <div className={styles.lineupsTabContent}>
                    {liveMatchData?.liveData?.lineUp &&
                      liveMatchData.liveData.lineUp.map((team, teamIndex) => (
                        <div key={teamIndex} className={styles.teamLineup}>
                          <div className={styles.teamLineupHeader}>
                            <h3>{team.teamName}</h3>
                            <div className={styles.formationBadge}>
                              Formation: {team.formationUsed}
                            </div>
                          </div>

                          <div className={styles.playersGrid}>
                            {team.players.map((player, playerIndex) => (
                              <div
                                key={playerIndex}
                                className={styles.playerCard}
                              >
                                <div className={styles.playerHeader}>
                                  <div className={styles.playerNumber}>
                                    {player.shirtNumber}
                                  </div>
                                  <div className={styles.playerName}>
                                    {player.matchName ||
                                      `${player.firstName} ${player.lastName}`}
                                  </div>
                                  {player.captain === "yes" && (
                                    <span className={styles.captainBadge}>
                                      C
                                    </span>
                                  )}
                                </div>
                                <div className={styles.playerPosition}>
                                  {player.position}
                                  {player.positionSide &&
                                    ` (${player.positionSide})`}
                                </div>
                                <div className={styles.playerStats}>
                                  <div className={styles.statItem}>
                                    <span className="material-icons">
                                      timer
                                    </span>
                                    {player.statistics?.minsPlayed || 0}'
                                  </div>
                                  {player.position === "Goalkeeper" && (
                                    <div className={styles.statItem}>
                                      <span className="material-icons">
                                        sports_soccer
                                      </span>
                                      {player.statistics?.saves || 0} saves
                                    </div>
                                  )}
                                  {player.position !== "Goalkeeper" && (
                                    <div className={styles.statItem}>
                                      <span className="material-icons">
                                        sports_soccer
                                      </span>
                                      {player.statistics?.goals || 0} goals
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                  </div>
                )}

                {/* Statistics Tab */}
                {modalActiveTab === "stats" && (
                  <div className={styles.statsTabContent}>
                    {liveMatchData?.liveData?.lineUp &&
                      liveMatchData.liveData.lineUp.map((team, teamIndex) => (
                        <div key={teamIndex} className={styles.teamStats}>
                          <h3>{team.teamName} - Player Statistics</h3>
                          <div className={styles.statsTable}>
                            <div className={styles.statsHeader}>
                              <div>Player</div>
                              <div>Position</div>
                              <div>Minutes</div>
                              <div>Goals</div>
                              <div>Assists</div>
                              <div>Passes</div>
                              <div>Pass Accuracy</div>
                              <div>Duels Won</div>
                              <div>Cards</div>
                            </div>
                            {team.players.map((player, playerIndex) => (
                              <div
                                key={playerIndex}
                                className={styles.statsRow}
                              >
                                <div className={styles.playerInfo}>
                                  <span className={styles.playerNumber}>
                                    {player.shirtNumber}
                                  </span>
                                  <span className={styles.playerName}>
                                    {player.matchName}
                                  </span>
                                  {player.captain === "yes" && (
                                    <span className={styles.captainBadge}>
                                      C
                                    </span>
                                  )}
                                </div>
                                <div>{player.position}</div>
                                <div>{player.statistics?.minsPlayed || 0}'</div>
                                <div>{player.statistics?.goals || 0}</div>
                                <div>{player.statistics?.assists || 0}</div>
                                <div>{player.statistics?.totalPass || 0}</div>
                                <div>
                                  {player.statistics?.totalPass > 0
                                    ? Math.round(
                                        ((player.statistics?.accuratePass ||
                                          0) /
                                          player.statistics.totalPass) *
                                          100
                                      )
                                    : 0}
                                  %
                                </div>
                                <div>{player.statistics?.duelWon || 0}</div>
                                <div>
                                  {(player.statistics?.yellowCards || 0) +
                                    (player.statistics?.redCards || 0)}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                  </div>
                )}

                {/* Fantasy Scores Tab */}
                {modalActiveTab === "fantasy" && (
                  <div className={styles.fantasyTabContent}>
                    {liveMatchData?.liveData?.lineUp &&
                      liveMatchData.liveData.lineUp.map((team, teamIndex) => (
                        <div key={teamIndex} className={styles.teamFantasy}>
                          <h3>{team.teamName} - Fantasy Scores</h3>
                          <div className={styles.fantasyTable}>
                            <div className={styles.fantasyHeader}>
                              <div>Player</div>
                              <div>Position</div>
                              <div>Total Score</div>
                              <div>Key Stats</div>
                            </div>
                            {team.players
                              .sort(
                                (a, b) =>
                                  (b.fantasyScore?.totalScore || 0) -
                                  (a.fantasyScore?.totalScore || 0)
                              )
                              .map((player, playerIndex) => (
                                <div
                                  key={playerIndex}
                                  className={styles.fantasyRow}
                                >
                                  <div className={styles.playerInfo}>
                                    <span className={styles.playerNumber}>
                                      {player.shirtNumber}
                                    </span>
                                    <span className={styles.playerName}>
                                      {player.matchName}
                                    </span>
                                    {player.captain === "yes" && (
                                      <span className={styles.captainBadge}>
                                        C
                                      </span>
                                    )}
                                  </div>
                                  <div>{player.position}</div>
                                  <div className={styles.fantasyScore}>
                                    {player.fantasyScore?.totalScore || 0}
                                  </div>
                                  <div className={styles.keyStats}>
                                    {player.position === "Goalkeeper" ? (
                                      <>
                                        <span>
                                          Saves: {player.statistics?.saves || 0}
                                        </span>
                                        <span>
                                          Goals Conceded:{" "}
                                          {player.statistics?.goalsConceded ||
                                            0}
                                        </span>
                                      </>
                                    ) : (
                                      <>
                                        <span>
                                          Goals: {player.statistics?.goals || 0}
                                        </span>
                                        <span>
                                          Assists:{" "}
                                          {player.statistics?.assists || 0}
                                        </span>
                                        <span>
                                          Passes:{" "}
                                          {player.statistics?.totalPass || 0}
                                        </span>
                                      </>
                                    )}
                                  </div>
                                </div>
                              ))}
                          </div>
                        </div>
                      ))}
                  </div>
                )}

                {/* Real-time indicator */}
                <div className={styles.liveUpdateStatus}>
                  <span className={styles.pulsingDot}></span>
                  Real-time updates active
                </div>
                </>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LiveScores;
